// external
import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Box, Divider, CircularProgress, Autocomplete, TextField, Button } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

// internal
import RaceLogEntryCard from '../../coe-race-log/race-log-entry-card';
import LinkRaceLogEntryForm from '../../coe-race-log/link-race-log-entry-form';
import AddRaceLogEntryForm, { AddEntryForm, AddEntryFormFields } from '../../coe-race-log/add-race-log-entry-form';
import COEYearDateRangeComponent from '../../coe-date-range/coe-year-date-range.component';
import useService from '../../../hooks/service.hook';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import RaceLogsService from '../../../services/race-logs.service';
import AnalyticsService from '../../../services/analytics.service';
import type {
  CreateEquipmentRaceLogPayload,
  UpdateRaceLogPayload,
  LinkEquipmentPayload,
} from '../../../models/race.models';
import { palette } from '../../../theme';
import { parseRaceDateTime } from '../../../helpers/general.helpers';
import type { RaceLogEntry } from '../../../models/race.models';

interface EquipmentRaceLogTabProps {
  equipmentId?: number;
}

const initialFormState = (equipmentId?: number) => ({
  selectedRaceId: '' as number | '',
  race_type: '',
  result: '' as number | '',
  equipment_id: equipmentId ?? '',
  equipment_performance: '' as number | '',
  race_note: '',
});

const EquipmentRaceLogTab: FC<EquipmentRaceLogTabProps> = ({ equipmentId }) => {
  const raceLogsService = useService(RaceLogsService);
  const analyticsService = useService(AnalyticsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const [entries, setEntries] = useState<RaceLogEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const [availableRaces, setAvailableRaces] = useState<RaceLogEntry[]>([]);
  const [racesLoading, setRacesLoading] = useState(false);
  const [form, setForm] = useState(() => initialFormState(equipmentId));
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<AddEntryForm>({
    race_name: '',
    race_date: '',
    race_location: '',
    race_distance: '',
    race_duration: '',
    race_sport: '',
    race_sport_type: '',
    race_type: '',
    result: '',
    equipment_performance: '',
    race_note: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string } | null>(null);
  const [annualStart, setAnnualStart] = useState<dayjs.Dayjs | null>(null);

  const computeDateRange = useCallback((currentPeriodStart: dayjs.Dayjs) => {
    const periodEnd = currentPeriodStart.add(1, 'year').subtract(1, 'day');
    setDateRange({
      start_date: currentPeriodStart.startOf('day').toISOString(),
      end_date: periodEnd.endOf('day').toISOString(),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!equipmentId) {
        setEntries([]);
        setAvailableRaces([]);
        setEntriesError(null);
        setEntriesLoading(false);
        return;
      }
      setEntriesLoading(true);
      setEntriesError(null);
      try {
        let effectiveStart = annualStart;
        if (!effectiveStart || !dateRange) {
          try {
            const res = await analyticsService.getAnalyticsAnnualStartDate();
            if (cancelled) return;
            const annualStartDateStr = res.data.data.annual_start_date;
            if (annualStartDateStr) {
              const storedDate = dayjs(annualStartDateStr);
              const currentYear = dayjs().year();
              effectiveStart = storedDate.year(currentYear);
              if (dayjs().isBefore(effectiveStart)) effectiveStart = effectiveStart.subtract(1, 'year');
            } else {
              effectiveStart = dayjs().startOf('year');
            }
          } catch {
            if (cancelled) return;
            effectiveStart = dayjs().startOf('year');
          }
          if (!effectiveStart) return;
          setAnnualStart(effectiveStart);
          computeDateRange(effectiveStart);
        }
        const rangeToUse = dateRange ?? {
          start_date: effectiveStart.startOf('day').toISOString(),
          end_date: effectiveStart.add(1, 'year').subtract(1, 'day').endOf('day').toISOString(),
        };
        const [entriesRes, racesRes] = await Promise.all([
          raceLogsService.getEquipmentRaceLogs(equipmentId, rangeToUse),
          raceLogsService.getAthleteRaceLogs(rangeToUse),
        ]);
        if (cancelled) return;
        const entriesData = entriesRes.data?.data ?? entriesRes.data;
        const racesData = racesRes.data?.data ?? racesRes.data;
        setEntries(Array.isArray(entriesData) ? (entriesData as RaceLogEntry[]) : []);
        setAvailableRaces(Array.isArray(racesData) ? (racesData as RaceLogEntry[]) : []);
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load race log entries.';
        setEntriesError(message);
        showSnackbarRef.current(message, 'error');
      } finally {
        if (!cancelled) {
          setEntriesLoading(false);
          setRacesLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [equipmentId, annualStart, dateRange, analyticsService, raceLogsService, computeDateRange]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, equipment_id: equipmentId ?? '' }));
  }, [equipmentId]);

  const handleFormChange = (field: keyof typeof form, value: string | number) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const handleCreateFormChange = (field: AddEntryFormFields, value: string | number) =>
    setCreateForm((prev) => ({ ...prev, [field]: value as never }));

  const selectedRace = form.selectedRaceId !== '' ? availableRaces.find((r) => r.id === form.selectedRaceId) : null;
  const filteredAvailableRaces = availableRaces.filter((race) => !entries.some((entry) => entry.id === race.id));

  const refreshEntries = async () => {
    if (!equipmentId || !dateRange) return;
    const res = await raceLogsService.getEquipmentRaceLogs(equipmentId, dateRange);
    const data = res.data?.data ?? res.data;
    setEntries(Array.isArray(data) ? data : []);
  };

  const handleAddEntry = async () => {
    if (!equipmentId || !selectedRace) return;
    if (form.equipment_performance === '') return showSnackbar('Please fill in equipment performance.', 'warning');
    setIsSubmitting(true);
    try {
      const payload: LinkEquipmentPayload = {
        equipment_id: equipmentId,
        equipment_performance: Number(form.equipment_performance),
        race_note: form.race_note.trim(),
      };
      await raceLogsService.linkEquipment(selectedRace.id, payload);
      await refreshEntries();
      setForm(initialFormState(equipmentId));
      showSnackbar('Race log entry linked.', 'success');
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } } };
      const message =
        err?.response?.status === 304
          ? 'Entry already exists for this race/equipment piece'
          : err?.response?.data?.message ?? 'Failed to link race log entry.';
      showSnackbar(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNewEntry = async () => {
    if (!equipmentId) return;
    if (!createForm.race_name.trim()) return showSnackbar('Please enter event name.', 'warning');
    if (!createForm.race_date) return showSnackbar('Please enter race date.', 'warning');
    if (!createForm.race_type.trim()) return showSnackbar('Please enter race type.', 'warning');
    if (!createForm.result.trim()) return showSnackbar('Please enter result.', 'warning');
    if (createForm.equipment_performance === '')
      return showSnackbar('Please fill in equipment performance.', 'warning');

    setIsSubmitting(true);
    try {
      const payload: CreateEquipmentRaceLogPayload = {
        race_name: createForm.race_name.trim(),
        race_date: createForm.race_date,
        race_location: createForm.race_location.trim(),
        race_distance: Number(createForm.race_distance || 0),
        race_duration: Number(createForm.race_duration || 0),
        race_sport: createForm.race_sport.trim(),
        race_sport_type: createForm.race_sport_type.trim(),
        race_type: createForm.race_type.trim(),
        result: Number(createForm.result),
        equipment_performance: Number(createForm.equipment_performance),
        race_note: createForm.race_note.trim(),
      };
      await raceLogsService.createEquipmentRaceLog(equipmentId, payload);
      await refreshEntries();
      setCreateForm({
        race_name: '',
        race_date: '',
        race_location: '',
        race_distance: '',
        race_duration: '',
        race_sport: '',
        race_sport_type: '',
        race_type: '',
        result: '',
        equipment_performance: '',
        race_note: '',
      });
      showSnackbar('Race log entry created.', 'success');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showSnackbar(err?.response?.data?.message ?? 'Failed to create race log entry.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRaceLog = async (entryId: number) => {
    if (!equipmentId) return;
    try {
      await raceLogsService.deleteRaceLog(entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      showSnackbar('Race log entry deleted.', 'success');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to delete race log entry.';
      showSnackbar(message, 'error');
    }
  };

  const handleUpdateRaceLog = async (entryId: number, payload: UpdateRaceLogPayload) => {
    if (!equipmentId) return;
    try {
      await raceLogsService.updateRaceLog(entryId, payload);
      await refreshEntries();
      showSnackbar('Race log entry updated.', 'success');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to update race log entry.';
      showSnackbar(message, 'error');
    }
  };

  return (
    <Box sx={{ minHeight: 'fit-content', position: 'relative' }}>
      {/* Season navigator */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <COEYearDateRangeComponent
          value={annualStart ?? dayjs().startOf('year')}
          setValue={(newValue: dayjs.Dayjs | null) => {
            if (!newValue) return;
            setAnnualStart(newValue);
            computeDateRange(newValue);
          }}
        />
      </Box>

      {/* Race entries */}
      <Box sx={{ maxHeight: 360, overflowY: 'auto', mb: 2 }}>
        {entriesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : entriesError ? (
          <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>{entriesError}</Typography>
        ) : entries.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>No race log entries yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {entries.map((entry) => (
              <RaceLogEntryCard
                key={entry.id}
                entry={entry}
                onEdit={handleUpdateRaceLog}
                onDelete={handleDeleteRaceLog}
                onOpenTraining={(trainingId) => navigate(`/home?trainingId=${trainingId}&trainingType=athlete`)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Link section */}
      <Divider sx={{ mb: 1.5 }} />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: palette.text.secondary,
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        Link race log entry
      </Typography>
      <Box sx={{ mb: 1.5 }}>
        <Autocomplete
          size="small"
          options={filteredAvailableRaces}
          loading={racesLoading}
          getOptionLabel={(race) => {
            const { dateDisplay } = parseRaceDateTime(race.race_date);
            return `${race.race_name} — ${dateDisplay}, ${race.race_location}`;
          }}
          value={
            form.selectedRaceId === ''
              ? null
              : filteredAvailableRaces.find((race) => race.id === form.selectedRaceId) ?? null
          }
          onChange={(_, value) => handleFormChange('selectedRaceId', value ? value.id : '')}
          renderInput={(params) => <TextField {...params} label="Event" placeholder="Search event" />}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
      </Box>

      {selectedRace && (
        <LinkRaceLogEntryForm
          race={selectedRace}
          form={{
            race_type: form.race_type,
            result: form.result,
            equipment_performance: form.equipment_performance,
            race_note: form.race_note,
          }}
          onFormChange={handleFormChange}
          onAddEntry={handleAddEntry}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Create section */}
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Create new entry
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowCreateForm((v) => !v)}
          sx={{ fontSize: 11, borderRadius: '6px', textTransform: 'none', py: 0.25, px: 1 }}
        >
          {showCreateForm ? 'Hide form' : 'Show form'}
        </Button>
      </Box>
      {showCreateForm && (
        <AddRaceLogEntryForm
          form={createForm}
          onFormChange={handleCreateFormChange}
          onAddEntry={handleCreateNewEntry}
          isSubmitting={isSubmitting}
        />
      )}
    </Box>
  );
};

export default EquipmentRaceLogTab;
